/* TFS delivery-estimate — shared date math for every theme surface
   (PDP buy box, cart page line items, cart drawer).

   Settings come from the admin Shipping Rules → Delivery Estimates card via
   GET /api/fitment/delivery-config (fetched through window.TFSFitment.api
   when the fitment app embed is present). The constants below are only the
   fallback when the API is unreachable. Mirrors
   extensions/fitment-extension/assets/product-grid.js — keep in sync:
   - window: windowMinDays-windowMaxDays business days from the ship base
     (custom drill: +customDrillExtraDays)
   - business day = Mon-Fri, minus US carrier-observed shipping holidays
     when excludeHolidays (New Year's, Memorial Day, July 4, Labor Day,
     Thanksgiving, Christmas, Sat->Fri / Sun->Mon observed shifts)
   - ship base: today (ET) before the cutoff on a business day, otherwise
     the next business day
   Dates are computed client-side because pages and API responses are cached.

   Fills every `[data-delivery-estimate]` element: writes the range into its
   `[data-delivery-dates]` child, marks it `data-delivery-ready`, and removes
   `hidden`. Elements with `data-custom-drill` get the extra days. */
(function () {
  'use strict';

  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  var config = {
    cutoffHour: 13,
    cutoffMinute: 0,
    windowMinDays: 3,
    windowMaxDays: 6,
    customDrillExtraDays: 3,
    excludeHolidays: true
  };

  function applyConfig(c) {
    if (!c || typeof c !== 'object') return;
    if (typeof c.cutoffTime === 'string' && /^\d{2}:\d{2}/.test(c.cutoffTime)) {
      config.cutoffHour = parseInt(c.cutoffTime.slice(0, 2), 10);
      config.cutoffMinute = parseInt(c.cutoffTime.slice(3, 5), 10);
    }
    if (c.windowMinDays != null) config.windowMinDays = parseInt(c.windowMinDays, 10) || config.windowMinDays;
    if (c.windowMaxDays != null) config.windowMaxDays = parseInt(c.windowMaxDays, 10) || config.windowMaxDays;
    if (c.customDrillExtraDays != null && !isNaN(parseInt(c.customDrillExtraDays, 10))) {
      config.customDrillExtraDays = parseInt(c.customDrillExtraDays, 10);
    }
    if (c.excludeHolidays != null) config.excludeHolidays = c.excludeHolidays !== false;
  }

  var holidayCache = {};
  function holidaySetForYear(y) {
    if (holidayCache[y]) return holidayCache[y];
    var set = {};
    function key(d) { return d.getUTCFullYear() + '-' + (d.getUTCMonth() + 1) + '-' + d.getUTCDate(); }
    function addObserved(m, day) {
      var d = new Date(Date.UTC(y, m, day, 12));
      var dow = d.getUTCDay();
      if (dow === 6) d.setUTCDate(d.getUTCDate() - 1);
      else if (dow === 0) d.setUTCDate(d.getUTCDate() + 1);
      set[key(d)] = true;
    }
    function addNthDow(m, dow, n) {
      var d;
      if (n > 0) {
        d = new Date(Date.UTC(y, m, 1, 12));
        d.setUTCDate(1 + ((dow - d.getUTCDay() + 7) % 7) + (n - 1) * 7);
      } else {
        d = new Date(Date.UTC(y, m + 1, 0, 12));
        d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() - dow + 7) % 7));
      }
      set[key(d)] = true;
    }
    addObserved(0, 1);    // New Year's Day
    addNthDow(4, 1, -1);  // Memorial Day — last Monday of May
    addObserved(6, 4);    // Independence Day
    addNthDow(8, 1, 1);   // Labor Day — first Monday of September
    addNthDow(10, 4, 4);  // Thanksgiving — fourth Thursday of November
    addObserved(11, 25);  // Christmas
    if (new Date(Date.UTC(y + 1, 0, 1, 12)).getUTCDay() === 6) set[y + '-12-31'] = true;
    holidayCache[y] = set;
    return set;
  }

  function isWorkdayUTC(d) {
    var dow = d.getUTCDay();
    if (dow === 0 || dow === 6) return false;
    if (!config.excludeHolidays) return true;
    return !holidaySetForYear(d.getUTCFullYear())[d.getUTCFullYear() + '-' + (d.getUTCMonth() + 1) + '-' + d.getUTCDate()];
  }

  function addBusinessDays(baseUtc, n) {
    var d = new Date(baseUtc.getTime());
    var added = 0;
    while (added < n) {
      d.setUTCDate(d.getUTCDate() + 1);
      if (isWorkdayUTC(d)) added++;
    }
    return d;
  }

  function getEasternNow() {
    try {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: 'numeric', hour12: false
      }).formatToParts(new Date());
      var map = {};
      for (var i = 0; i < parts.length; i++) map[parts[i].type] = parts[i].value;
      return {
        y: parseInt(map.year, 10),
        m: parseInt(map.month, 10) - 1,
        d: parseInt(map.day, 10),
        hour: parseInt(map.hour, 10) % 24,
        minute: parseInt(map.minute, 10) || 0
      };
    } catch (e) {
      var now = new Date();
      return { y: now.getFullYear(), m: now.getMonth(), d: now.getDate(), hour: now.getHours(), minute: now.getMinutes() };
    }
  }

  /* "Jun 16 - Jun 19" for the standard window; custom drill adds the
     configured extra days. */
  function windowText(customDrill) {
    var et = getEasternNow();
    var base = new Date(Date.UTC(et.y, et.m, et.d, 12, 0, 0));
    var pastCutoff = et.hour > config.cutoffHour
      || (et.hour === config.cutoffHour && et.minute >= config.cutoffMinute);
    if (pastCutoff || !isWorkdayUTC(base)) base = addBusinessDays(base, 1);
    var extra = customDrill ? config.customDrillExtraDays : 0;
    var from = addBusinessDays(base, config.windowMinDays + extra);
    var to = addBusinessDays(base, config.windowMaxDays + extra);
    return MONTHS[from.getUTCMonth()] + ' ' + from.getUTCDate()
      + ' - ' + MONTHS[to.getUTCMonth()] + ' ' + to.getUTCDate();
  }

  function fillAll(root) {
    var els = (root || document).querySelectorAll('[data-delivery-estimate]:not([data-delivery-ready])');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var target = el.querySelector('[data-delivery-dates]');
      if (target) target.textContent = windowText(el.hasAttribute('data-custom-drill'));
      el.setAttribute('data-delivery-ready', 'true');
      el.removeAttribute('hidden');
    }
  }

  window.TFSDelivery = { windowText: windowText, fillAll: fillAll, applyConfig: applyConfig };

  /* Fetch admin settings before the first fill. The fitment app embed
     (window.TFSFitment) provides the API base; it loads async, so retry
     briefly. Any failure falls back to the baked-in defaults. */
  function loadConfigThenFill(attempt) {
    var F = window.TFSFitment;
    if (!F || !F.api) {
      if (attempt < 4) return setTimeout(function () { loadConfigThenFill(attempt + 1); }, 400);
      return fillAll();
    }
    var done = false;
    var finish = function () { if (!done) { done = true; fillAll(); } };
    setTimeout(finish, 1500); // don't hold the UI hostage to a slow request
    F.api.get('/delivery-config').then(function (data) {
      applyConfig(data && data.config);
      finish();
    }).catch(finish);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { loadConfigThenFill(0); });
  } else {
    loadConfigThenFill(0);
  }
})();
